// src/utils/authFetch.ts
// Utility for making authenticated API calls that works in both normal and iframe contexts
// In iframe contexts (GHL), cookies may be blocked, so we use Authorization header

import { createClient } from '@/utils/supabase/client';

// Storage key for CRM auth token (set by crm-auth-callback)
const GHL_TOKEN_KEY = 'ssp:ghl_access_token';

/**
 * Store the access token for GHL iframe auth
 */
export function setGHLAccessToken(token: string) {
  if (typeof window !== 'undefined') {
    window.localStorage.setItem(GHL_TOKEN_KEY, token);
  }
}

/**
 * Get the access token for GHL iframe auth
 */
export function getGHLAccessToken(): string | null {
  if (typeof window === 'undefined') return null;
  return window.localStorage.getItem(GHL_TOKEN_KEY);
}

/**
 * Clear the GHL access token
 */
export function clearGHLAccessToken() {
  if (typeof window !== 'undefined') {
    window.localStorage.removeItem(GHL_TOKEN_KEY);
  }
}

/**
 * Get access token from cookie
 */
function getTokenFromCookie(): string | null {
  if (typeof document === 'undefined') return null;
  
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === 'sb-access-token' && value) {
      return decodeURIComponent(value);
    }
  }
  return null;
}

/** Buffer (seconds) — refresh proactively before the token actually expires */
const REFRESH_BUFFER_SEC = 120; // 2 minutes

/**
 * Get the current access token from various sources (best → worst):
 * 1. Supabase singleton session — refreshes proactively if near expiry
 * 2. localStorage CRM token (set during SSO, may be stale)
 * 3. Cookie token (set by middleware)
 */
async function getAccessToken(): Promise<string | null> {
  // 1. Supabase singleton client
  try {
    const supabase = createClient();
    const { data: { session } } = await supabase.auth.getSession();

    if (session?.access_token) {
      const nowSec = Math.floor(Date.now() / 1000);
      const expiresAt = session.expires_at ?? 0;

      // If the token expires within the buffer window, refresh it
      if (expiresAt > 0 && expiresAt - nowSec < REFRESH_BUFFER_SEC) {
        const { data: { session: refreshed }, error: refreshErr } =
          await supabase.auth.refreshSession();

        if (!refreshErr && refreshed?.access_token) {
          return refreshed.access_token;
        }
        // Refresh failed — fall through with the (nearly) expired token;
        // the server may still accept it for a short grace window.
        console.warn('[authFetch] Token refresh failed, using existing token:', refreshErr?.message);
      }

      return session.access_token;
    }
  } catch (e) {
    console.warn('[authFetch] Supabase session error:', e);
  }

  // 2. localStorage CRM token (fallback for iframe SSO contexts)
  const ghlToken = getGHLAccessToken();
  if (ghlToken) {
    return ghlToken;
  }

  // 3. Cookie token (set by middleware after ticket validation)
  const cookieToken = getTokenFromCookie();
  if (cookieToken) {
    return cookieToken;
  }

  console.warn('[authFetch] No token found from any source');
  return null;
}

/**
 * Make an authenticated fetch request
 * Automatically includes Authorization header with access token
 */
export async function authFetch(
  url: string,
  options: RequestInit = {}
): Promise<Response> {
  const token = await getAccessToken();

  const headers = new Headers(options.headers);
  
  // Always set content type for JSON if not already set
  if (!headers.has('Content-Type') && options.method !== 'GET') {
    headers.set('Content-Type', 'application/json');
  }

  // Add Authorization header if we have a token
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  return fetch(url, {
    ...options,
    headers,
    credentials: 'include', // Still try cookies as backup
  });
}

/**
 * Check if we're in an iframe
 */
export function isInIframe(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.self !== window.top;
  } catch {
    return true; // If we can't access window.top, we're probably in an iframe
  }
}
